import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import AddShoppingCartTwoToneIcon from '@mui/icons-material/AddShoppingCartTwoTone';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LayersIcon from '@mui/icons-material/Layers';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PageHeader from '../common/PageHeader';
import SearchField from '../common/SearchField';
import NoDataFound from '../Utils/NoDataFound';
import ProductForm from './ProductForm';
import { useGetBrandsQuery, useGetModelsByBrandQuery } from '../../api/master/MasterApi';
import {
  useDeleteProductMutation,
  useGetFilterPropertiesQuery,
  useGetProductPageMutation,
  useLazyGetProductByIdQuery,
} from '../../api/product/ProductApi';
import { MasterRow } from '../../types/master';
import { Product, ProductBatch } from '../../types/product';
import { longDate } from '../../utils/dates';
import { can } from '../../utils/auth';
import { useSnackbar } from '../../utils/snackbar';

const money = (value?: number | null) => (value === null || value === undefined ? '-' : Number(value).toFixed(2));

/** The product master: handsets and accessories in one list, with their batches. */
const ProductList = () => {
  const { openSnackbar } = useSnackbar();
  const [view, setView] = useState<'LIST' | 'FORM'>('LIST');
  const [editing, setEditing] = useState<Product | null>(null);

  const [searchValue, setSearchValue] = useState('');
  const [brandId, setBrandId] = useState('');
  const [modelId, setModelId] = useState('');

  /**
   * Properties being filtered on, as key/value pairs rather than one box each.
   *
   * <p>A box per property does not survive the shop adding specifications - three
   * becomes eight becomes twelve, and the filter bar eats the screen. One picker and
   * one value box add a chip, however many properties exist, and the picker's list
   * comes from the server so a newly recorded specification appears in it by itself.
   */
  const [properties, setProperties] = useState<Record<string, string>>({});
  const [propertyKey, setPropertyKey] = useState('');
  const [propertyValue, setPropertyValue] = useState('');
  const [rows, setRows] = useState<MasterRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [batchesOf, setBatchesOf] = useState<Product | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: brands = [] } = useGetBrandsQuery();
  // Models only make sense under a brand, so the box stays empty until one is picked.
  const { data: models = [] } = useGetModelsByBrandQuery(Number(brandId), { skip: !brandId });
  const { data: filterProperties = [] } = useGetFilterPropertiesQuery();
  const [getProductPage] = useGetProductPageMutation();
  const [getProductById] = useLazyGetProductByIdQuery();
  const [deleteProduct] = useDeleteProductMutation();

  const mayAdd = can('MASTERS', 'create');
  const mayEdit = can('MASTERS', 'edit');
  const mayDelete = can('MASTERS', 'delete');

  const propertyEntries = Object.entries(properties);
  const anyFilter = !!(searchValue || brandId || modelId || propertyEntries.length);

  /** The label the shop reads for a key, falling back to the key itself. */
  const labelFor = (key: string) => filterProperties.find((property) => property.key === key)?.label ?? key;

  const clearFilters = () => {
    setSearchValue('');
    setBrandId('');
    setModelId('');
    setProperties({});
    setPropertyKey('');
    setPropertyValue('');
  };

  const addProperty = () => {
    if (!propertyKey || !propertyValue.trim()) return;
    setProperties((previous) => ({ ...previous, [propertyKey]: propertyValue.trim() }));
    // The picker stays where it is - two properties in a row are usually adjacent in
    // the list - but the value clears, because the next one is a different value.
    setPropertyValue('');
  };

  const removeProperty = (key: string) =>
    setProperties((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });

  const load = () => {
    if (timer.current) clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const response = await getProductPage({
          searchValue,
          brandId: brandId ? Number(brandId) : null,
          modelId: modelId ? Number(modelId) : null,
          properties,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    if (view === 'LIST') load();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, brandId, modelId, properties, paginationModel.page, paginationModel.pageSize, view]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, brandId, modelId, properties]);

  const openForm = async (row?: MasterRow) => {
    if (!row?.id) {
      setEditing(null);
      setView('FORM');
      return;
    }
    try {
      setEditing(await getProductById(Number(row.id)).unwrap());
      setView('FORM');
    } catch {
      openSnackbar('Could not open the product', 'error');
    }
  };

  const openBatches = async (row: MasterRow) => {
    try {
      setBatchesOf(await getProductById(Number(row.id)).unwrap());
    } catch {
      openSnackbar('Could not read the batches', 'error');
    }
  };

  const remove = async (row: MasterRow) => {
    if (!window.confirm(`Delete ${row.productName}?`)) return;
    try {
      await deleteProduct(Number(row.id)).unwrap();
      openSnackbar('Product deleted', 'success');
      load();
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not delete the product', 'error');
    }
  };

  const columns: GridColDef[] = [
    { field: 'productName', headerName: 'Product', flex: 1.6, minWidth: 220 },
    { field: 'brandName', headerName: 'Brand', flex: 1, minWidth: 110, align: 'center', headerAlign: 'center' },
    { field: 'modelName', headerName: 'Model', flex: 1, minWidth: 110, align: 'center', headerAlign: 'center' },
    {
      field: 'barcode',
      headerName: 'Barcode',
      flex: 1,
      minWidth: 140,
      align: 'center',
      headerAlign: 'center',
      // Worth seeing at a glance: a product without one cannot be scanned, and the
      // shop will want to fix that before the counter is busy.
      renderCell: (row) =>
        row.value ? (
          <Chip size="small" variant="outlined" label={String(row.value)} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            None
          </Typography>
        ),
    },
    {
      field: 'serialTracked',
      headerName: 'Type',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (row) => (
        <Chip
          size="small"
          variant="outlined"
          color={row.value ? 'primary' : 'default'}
          label={row.value ? 'Serial number' : 'No serial number'}
        />
      ),
    },
    {
      // Right, not centre. Centred numbers of different lengths put their digits in
      // different places down the column - 1,200 and 18,999 never line up - so the eye
      // cannot compare them by scanning. Aligned right, units sit under units. The
      // batch table further down this same screen already does it this way.
      field: 'sellingPrice',
      headerName: 'Price',
      flex: 1,
      minWidth: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => money(row.value as number),
    },
    {
      field: 'availableStock',
      headerName: 'In stock',
      flex: 1,
      minWidth: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => (
        <Typography variant="body2" color={Number(row.value) === 0 ? 'error.main' : 'text.primary'} fontWeight={Number(row.value) === 0 ? 700 : 400}>
          {String(row.value ?? 0)}
        </Typography>
      ),
    },
    {
      field: 'Action',
      headerName: 'Action',
      flex: 1,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (row) => (
        <>
          <IconButton size="small" onClick={() => openBatches(row.row)} aria-label="Batches" title="Batches">
            <LayersIcon fontSize="small" />
          </IconButton>
          {mayEdit && (
            <IconButton size="small" onClick={() => openForm(row.row)} aria-label="Edit">
              <EditTwoToneIcon fontSize="small" />
            </IconButton>
          )}
          {mayDelete && (
            <IconButton size="small" color="error" onClick={() => remove(row.row)} aria-label="Delete">
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  if (view === 'FORM') {
    return (
      <ProductForm
        product={editing}
        onClose={() => {
          setView('LIST');
          setEditing(null);
        }}
      />
    );
  }

  return (
    <Box>
      <PageHeader title="Products" subtitle="Handsets and accessories, with the batches behind them">
        {mayAdd && (
          <Button variant="contained" size="small" startIcon={<AddShoppingCartTwoToneIcon />} onClick={() => openForm()}>
            Add Product
          </Button>
        )}
      </PageHeader>

      {/* The search box takes the words in any order; the boxes beside it pin down an
          exact property. Both narrow the same list, so they can be used together. */}
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start" sx={{ mb: 2 }}>
        <SearchField
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Any words: brand, model, colour, RAM, storage, barcode..."
          width={330}
        />
        <TextField
          select
          label="Brand"
          size="small"
          sx={{ width: 180 }}
          value={brandId}
          onChange={(event) => {
            setBrandId(event.target.value);
            // The old model belonged to the old brand.
            setModelId('');
          }}
        >
          <MenuItem value="">All brands</MenuItem>
          {brands.map((brand) => (
            <MenuItem key={brand.id} value={brand.id}>
              {brand.brandName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Model"
          size="small"
          sx={{ width: 170 }}
          disabled={!brandId}
          helperText={brandId ? undefined : 'Pick a brand first'}
          value={modelId}
          onChange={(event) => setModelId(event.target.value)}
        >
          <MenuItem value="">All models</MenuItem>
          {models.map((model) => (
            <MenuItem key={model.id} value={model.id}>
              {model.modelName}
            </MenuItem>
          ))}
        </TextField>
        {/* One picker and one value box, whatever the shop records. Colour, RAM and
            Storage come built in; anything else the shop has entered as a specification
            on a product arrives in this list from the server. */}
        <TextField
          select
          label="Specification"
          size="small"
          sx={{ width: 165 }}
          value={propertyKey}
          onChange={(event) => setPropertyKey(event.target.value)}
        >
          {filterProperties.map((property) => (
            <MenuItem key={property.key} value={property.key}>
              {property.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Is"
          size="small"
          sx={{ width: 140 }}
          placeholder={propertyKey === 'ram' || propertyKey === 'storage' ? '128' : 'Mint'}
          disabled={!propertyKey}
          value={propertyValue}
          onChange={(event) => setPropertyValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addProperty();
            }
          }}
        />
        <Tooltip title="Add this specification to the filter">
          <IconButton
            color="primary"
            disabled={!propertyKey || !propertyValue.trim()}
            onClick={addProperty}
            aria-label="Add specification filter"
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 40, height: 40, mt: 0.5 }}
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </Tooltip>
        {anyFilter && (
          <Button size="small" color="inherit" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ mt: 0.5 }}>
            Clear
          </Button>
        )}
        <Box flexGrow={1} />
        <Chip variant="outlined" label={`${rowCount} product(s)`} sx={{ mt: 0.5 }} />
      </Stack>

      {/* What is being filtered on right now, each removable on its own. */}
      {propertyEntries.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
          {propertyEntries.map(([key, value]) => (
            <Chip
              key={key}
              size="small"
              color="primary"
              variant="outlined"
              label={`${labelFor(key)}: ${value}`}
              onDelete={() => removeProperty(key)}
            />
          ))}
        </Stack>
      )}

      {rows.length === 0 && !loading ? (
        // A search that matched nothing is not an empty product master, and offering
        // "Add Product" as the answer to it sends the shop down the wrong path.
        <NoDataFound
          onCreateClick={() => (anyFilter ? clearFilters() : mayAdd ? openForm() : load())}
          buttonName={anyFilter ? 'Clear Search' : mayAdd ? 'Add Product' : 'Refresh'}
          message={anyFilter ? 'Nothing matched this search' : 'No products yet'}
        />
      ) : (
        <Card sx={{ p: 1 }}>
          <DataGrid
            columns={columns}
            rows={rows}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            loading={loading}
            sx={{ height: '68vh', border: 0 }}
          />
        </Card>
      )}

      <Dialog open={!!batchesOf} onClose={() => setBatchesOf(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">
            {batchesOf?.productName}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Every consignment received, newest first. The same product bought twice keeps two prices.
          </Typography>
          {/*
            "Bought 10" next to "In stock 0" was read as "there are 10 here" twice over -
            once by the tester, once in review. The two numbers answer different questions,
            so the dialog now says the answer outright before the table is reached.
          */}
          {batchesOf && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              <b>In stock now: {Number(batchesOf.availableStock ?? 0)}</b>
              {' '}&middot; {(batchesOf.batches ?? []).reduce((sum: number, batch: ProductBatch) => sum + Number(batch.quantity ?? 0), 0)} purchased in total
              {Number(batchesOf.availableStock ?? 0) === 0 && (batchesOf.batches ?? []).length > 0
                ? ' - every piece has been sold'
                : ''}
            </Typography>
          )}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Batch</TableCell>
                <TableCell>Received</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Sells for</TableCell>
                <TableCell align="center">Qty purchased</TableCell>
                <TableCell align="center">In stock now</TableCell>
                <TableCell>Warranty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(batchesOf?.batches ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Nothing purchased yet.
                  </TableCell>
                </TableRow>
              )}
              {(batchesOf?.batches ?? []).map((batch: ProductBatch) => (
                <TableRow key={batch.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{batch.batchNo}</TableCell>
                  <TableCell>{longDate(batch.receivedDate)}</TableCell>
                  <TableCell align="right">{money(batch.purchasePrice)}</TableCell>
                  <TableCell align="right">{money(batch.sellingPrice)}</TableCell>
                  <TableCell align="center">{batch.quantity}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      variant="outlined"
                      color={Number(batch.quantityRemaining) > 0 ? 'success' : 'default'}
                      label={batch.quantityRemaining}
                    />
                  </TableCell>
                  <TableCell>{longDate(batch.warrantyEndDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setBatchesOf(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductList;
